import { useCallback } from 'react';

import Sentry from '@lib/analytics/sentry';
import useAuth from '@lib/hooks/useAuth';
import { useApplicationState } from '@lib/state';
import { readE2eSkipKeys } from '@lib/utils/e2eSkipKeys';

interface CaptchaWebHook {
    /**
     * Promise that resolves with a fresh captcha token. The singleton `<Captcha>` at
     * the app root (`pages/_app.tsx`) handles widget execution; this hook signals it
     * via zustand state and subscribes for the resulting token.
     *
     * Two bounded phases, each with its OWN error so Sentry can tell them apart:
     *   1. `Error('Captcha widget failed to load')` — the hCaptcha script never
     *      reported ready within `WIDGET_READY_TIMEOUT_MS`. The token request was
     *      never even sent (blocked script, dead network, CSP/extension).
     *   2. `Error('Captcha token not received in time')` — the widget IS ready and
     *      `execute()` ran, but no token came back within `TOKEN_TIMEOUT_MS`,
     *      EXCEPT while hCaptcha's visible challenge is open (see `setChallengeOpen`).
     */
    generateToken: () => Promise<string>;
}

/**
 * Deadline for the token ROUND-TRIP only — the widget is loaded and `execute()`
 * has run. The silent path resolves in ~1-2s, so this is a hang ceiling, not a
 * latency budget.
 *
 * This deliberately no longer covers widget BOOTSTRAP. It used to: the clock
 * started on the user's tap and ran through script injection, download and
 * `onLoad`, so a slow cold start burned the token budget on a request that had
 * not been sent yet — indistinguishable in Sentry from a genuine token hang.
 * `WIDGET_READY_TIMEOUT_MS` owns that wait now.
 */
const TOKEN_TIMEOUT_MS = 15000;

/**
 * Deadline for the widget to report ready (hCaptcha's `onLoad`).
 *
 * 15s, matching `TOKEN_TIMEOUT_MS`. This was briefly narrowed to 5s on the reasoning that
 * bootstrap measures ~600-1000ms on a production build, so anything slower is unreachable
 * rather than slow. That reasoning is a fast-network measurement: on slow 3G or a
 * high-latency mobile link, inject + fetch + parse + `onLoad` can exceed 5s, and those
 * users would fail where they previously succeeded — the exact symptom this ticket is
 * about. Splitting one deadline into two is what made the two failures distinguishable in
 * Sentry; shortening one of them was never part of that and is not needed for it.
 *
 * DO NOT tune either captcha deadline as part of a fix. A captcha that times out is
 * discarding or never sending a token; the timer is not the bug. See
 * `getCaptchaTiming()` — p95 `lastWaitedForWidgetMs` is the number to look at first.
 */
const WIDGET_READY_TIMEOUT_MS = 15000;

/**
 * Captcha lifecycle timings, in milliseconds.
 *
 * WHY: the whole point of hoisting `<Captcha>` above the null-gating providers and
 * preconnecting the hCaptcha hosts is to shrink `widgetReadyMs`. Without a number
 * that claim is unfalsifiable, and the next person to re-nest the widget under a
 * provider would reintroduce the regression silently. These also answer the
 * question the old single-deadline code could not: when a captcha fails, was the
 * app waiting on the SCRIPT or on the TOKEN?
 *
 *  - `widgetReadyMs`          page load → hCaptcha `onLoad`. Set once per page.
 *  - `lastWaitedForWidgetMs`  how long the most recent request blocked on bootstrap
 *                             (0 when the widget was already ready — the normal case).
 *  - `lastTokenMs`            round-trip of the most recent token request.
 */
export interface CaptchaTiming {
    widgetReadyMs?: number;
    lastWaitedForWidgetMs?: number;
    lastTokenMs?: number;
}

const timing: CaptchaTiming = {};

/** Snapshot for Sentry `extra`, e2e measurement, and manual inspection. */
export const getCaptchaTiming = (): CaptchaTiming => ({ ...timing });

/**
 * `performance.now()` is relative to `timeOrigin` (≈ navigation start), so it reads
 * directly as "milliseconds since this page started loading". Guarded because this
 * module is imported from the shared graph.
 */
const nowMs = (): number | undefined =>
    typeof globalThis.performance?.now === 'function' ? globalThis.performance.now() : undefined;

/**
 * Monotonic id of the token request currently awaiting a value; 0 when idle.
 *
 * WHY THIS EXISTS instead of comparing token VALUES: react-hcaptcha's `onVerify`
 * delivers `hcaptcha.getResponse(captchaId)` — the widget's STORED response, not a
 * freshly minted string. hCaptcha responses live ~2 minutes, so a request inside that
 * window can legitimately receive the SAME string as the previous one. A value guard
 * therefore cannot tell "my token" from "not my token": the original one discarded
 * perfectly valid responses and left the caller waiting out the full deadline for a
 * replacement that was never coming — "Couldn't verify, please try again" (JSAD-7152).
 *
 * Freshness is not this module's job at all. `<Captcha>` resets the widget before every
 * `execute()` (`resetWidget()` in `components/Captcha/index.web.tsx`), so a replay cannot
 * be produced in the first place. Do not reintroduce a value comparison here as a
 * backstop — it is guesswork about the widget's internal state and it is what caused the
 * bug.
 *
 * Keyed on the request rather than the value, and NOT on `captchaSignal`: the widget's
 * `onError` auto-retry issues a new signal, and that retry's token must still satisfy
 * the original caller.
 */
let requestSeq = 0;
let activeRequest = 0;

/**
 * Message on the rejection a request receives when a LATER `generateToken()` takes the
 * widget from it. Distinct from the two real failures ('widget failed to load', 'token not
 * received in time') so Sentry can separate "this user's action was replaced" from "the
 * captcha is broken" — the superseded case is expected under a double-tap and should not
 * pollute the dashboard this ticket exists to clean up.
 */
export const CAPTCHA_SUPERSEDED = 'Captcha request superseded';

/**
 * Cancels the in-flight request, if any. Registered by each request and cleared when it
 * settles.
 *
 * WHY: the widget is a singleton and can only serve one `execute()` at a time, and its
 * tokens are single-use, so two overlapping callers cannot share one. Before this, a
 * superseded request was merely IGNORED — its subscriber failed the ownership check below
 * and it sat until its own deadline fired, at which point it cleared `captchaSignal` and
 * `captchaToken` that by then belonged to the WINNER, stranding a request that had not
 * executed yet. It also always rejected, toasting "Couldn't verify" at a user whose action
 * had actually succeeded. Killing it up front costs it nothing it could still have won.
 */
let cancelActiveRequest: (() => void) | null = null;

/**
 * Is a caller currently waiting on a token?
 *
 * Read by the singleton `<Captcha>` so it never writes a token nobody asked for. An
 * unconsumed "orphan" token used to sit in `captchaToken` and poison the next request,
 * which is how a user's very FIRST submit could fail.
 */
export const isCaptchaRequestPending = (): boolean => activeRequest !== 0;

/**
 * Monotonic id of the caller request currently being served.
 *
 * Increments exactly ONCE per `generateToken()` (see `requestToken` below) and NOT on
 * either re-signal path — `<Captcha>`'s `onError` auto-retry, nor the duplicate-token
 * retry further down. That makes it the only safe key for re-arming the component's
 * error budget: `captchaSignal` changes on all three, so keying a reset to the signal
 * would re-arm the budget on the retry itself and restore the unbounded error loop the
 * cap exists to prevent.
 */
export const getCaptchaRequestSeq = (): number => requestSeq;

/**
 * Is hCaptcha's VISIBLE challenge (the image puzzle) currently on screen?
 *
 * Module-level rather than zustand state on purpose: the only writer is the
 * singleton `<Captcha>` and the only reader is the timeout below, both in the
 * same module graph on web. A store round-trip would buy re-render plumbing
 * nothing here needs.
 */
let isChallengeOpen = false;

/**
 * Called by the singleton `<Captcha>` from hCaptcha's `onOpen`/`onClose`.
 *
 * WHY: `TOKEN_TIMEOUT_MS` is sized for the SILENT path — a pure network
 * round-trip that resolves in ~1-2s, where 15s means "something hung". When
 * hCaptcha escalates to a visible challenge, a human has to read and solve an
 * image puzzle, which routinely takes longer than 15s. Firing the silent-path
 * deadline mid-solve rejected a token the user was still legitimately earning,
 * so the order never reached process-payment (2026-08-20 production incident).
 * While the challenge is open the deadline is suspended; closing it (dismissed,
 * or solved) restarts the clock so a token still can't hang forever.
 */
export const setChallengeOpen = (open: boolean) => {
    isChallengeOpen = open;
};

/**
 * Has the singleton widget fired `onLoad`? Same module-level rationale as
 * `isChallengeOpen`: single writer (`<Captcha>`), single reader (this module).
 */
let isWidgetReady = false;

/** Resolvers parked by `waitForWidget()` while the widget is still loading. */
const readyWaiters = new Set<() => void>();

/**
 * Called by the singleton `<Captcha>` from hCaptcha's `onLoad`.
 *
 * WHY this exists at all: `generateToken()` used to arm the token deadline
 * immediately, but `<Captcha>`'s effect cannot call `execute()` until the widget
 * is ready. A tap during bootstrap therefore spent the entire 15s waiting for a
 * request that was never issued, then reported "token not received in time" —
 * blaming the round-trip for a script-load problem. Gating on readiness keeps
 * the two failures distinguishable.
 */
export const setWidgetReady = (ready: boolean) => {
    isWidgetReady = ready;
    if (!ready) {
        return;
    }

    // First ready signal of the page is the bootstrap measurement. A breadcrumb
    // rather than its own event: it costs nothing, and it rides along on any later
    // captcha failure so the report says how long the script actually took.
    if (timing.widgetReadyMs === undefined) {
        const readyAt = nowMs();
        if (readyAt !== undefined) {
            timing.widgetReadyMs = readyAt;
            Sentry.addBreadcrumb({
                category: 'captcha',
                message: 'hCaptcha widget ready',
                level: 'info',
                data: { widgetReadyMs: Math.round(readyAt) }
            });
        }
    }
    // Copy before notifying: a waiter is free to start another request.
    const waiters = [...readyWaiters];
    readyWaiters.clear();
    waiters.forEach((notify) => notify());
};

/**
 * Resolve once the widget is ready, or reject after `WIDGET_READY_TIMEOUT_MS`.
 * Bounded on purpose — an unreachable hCaptcha script must surface as an error,
 * not a promise that never settles.
 *
 * PRECONDITION: only call this when `isWidgetReady` is false. It parks a waiter and waits
 * for the next `setWidgetReady(true)`, so calling it after `onLoad` has already fired
 * would wait for an event that is not coming again. The sole caller checks on the line
 * immediately before; keep it that way, or move the check in here.
 */
const waitForWidget = () =>
    new Promise<void>((resolve, reject) => {
        let timeoutId: ReturnType<typeof setTimeout>;
        const waitStart = nowMs();

        const recordWait = () => {
            const end = nowMs();
            if (waitStart !== undefined && end !== undefined) {
                timing.lastWaitedForWidgetMs = end - waitStart;
            }
        };

        const notify = () => {
            clearTimeout(timeoutId);
            recordWait();
            resolve();
        };

        readyWaiters.add(notify);

        timeoutId = setTimeout(() => {
            readyWaiters.delete(notify);
            recordWait();
            reject(new Error('Captcha widget failed to load'));
        }, WIDGET_READY_TIMEOUT_MS);
    });

export const useCaptchaWeb = (): CaptchaWebHook => {
    const { generateCaptchaSignal, clearCaptchaState } = useAuth();

    const requestToken = useCallback(() => {
        return new Promise<string>((resolve, reject) => {
            // Retire the incumbent before claiming the slot.
            //
            // Two requests can BOTH park in `waitForWidget()` — neither has registered a
            // canceller yet — and proceed together when the widget loads. Without this the
            // second would overwrite the first's canceller without ever invoking it, and
            // the first would leak: no token, no rejection, just a spinner until its own
            // deadline. Must precede the `activeRequest` assignment so the outgoing
            // request's `finish()` still recognises itself as the owner and cleans up.
            cancelActiveRequest?.();

            // Clear any stale "challenge open" state, IMMEDIATELY after the cancellation
            // above and before this request arms its own deadline.
            //
            // WHY the flag needs clearing: `isChallengeOpen` is module-global and only
            // cleared by the widget's own `onClose`/`onVerify`/`onError`/`onExpire`. If a
            // challenge opens and none of those ever fire — the user navigates away
            // mid-puzzle, the frame is torn down — it stays `true` forever, `armTimeout()`
            // re-arms in an infinite loop, and the promise NEVER settles: no token, no
            // rejection, no toast, no Sentry event, just a spinner. A stale `true` left by
            // checkout would hang a later login too.
            //
            // WHY it lives HERE, adjacent to the cancel, rather than in `generateToken()`:
            // the flag is shared, so resetting it un-suspends whoever is relying on it. If
            // a request holding a live puzzle were still alive at this point, this line
            // would reject a human who was still solving. Retiring the incumbent first
            // makes that impossible, and keeping the two statements adjacent means the
            // ordering cannot be broken by a later edit inserting an await between them.
            setChallengeOpen(false);

            const myRequest = ++requestSeq;
            activeRequest = myRequest;
            clearCaptchaState();
            generateCaptchaSignal();

            let settled = false;
            let timeoutId: ReturnType<typeof setTimeout>;
            const requestStart = nowMs();

            const finish = (fn: () => void) => {
                if (settled) {
                    return;
                }
                settled = true;
                if (activeRequest === myRequest) {
                    activeRequest = 0;
                    cancelActiveRequest = null;
                }
                unsubscribe();
                clearTimeout(timeoutId);
                const end = nowMs();
                if (requestStart !== undefined && end !== undefined) {
                    timing.lastTokenMs = end - requestStart;
                }
                fn();
            };

            // Re-arm rather than reject while the user is mid-puzzle: the
            // deadline only applies to time the app is waiting on itself, not
            // to time it is waiting on a human.
            const armTimeout = () => {
                timeoutId = setTimeout(() => {
                    if (isChallengeOpen) {
                        armTimeout();
                        return;
                    }
                    // Unconditional, and safe to be: a timer can only fire for a request
                    // that is still unsettled, and an unsettled request cannot have lost
                    // ownership — the one site that claims `activeRequest` cancels the
                    // incumbent first, and cancelling clears its timer. (An ownership
                    // check here would be unreachable, so it is not written.)
                    clearCaptchaState();
                    finish(() => reject(new Error('Captcha token not received in time')));
                }, TOKEN_TIMEOUT_MS);
            };

            // Registered AFTER `finish` exists so cancellation settles cleanly: it
            // unsubscribes, clears the timer, and rejects — without touching the slice,
            // which the superseding request now owns.
            cancelActiveRequest = () => {
                finish(() => reject(new Error(CAPTCHA_SUPERSEDED)));
            };

            const unsubscribe = useApplicationState.subscribe((state) => {
                const current = state.auth.captchaToken;
                if (!current) {
                    return;
                }
                // A later request supersedes this one; ignore writes that are no longer
                // ours rather than resolving two callers from one token.
                if (activeRequest !== myRequest) {
                    return;
                }

                const token = current.toString();
                clearCaptchaState();
                finish(() => resolve(token));
            });

            armTimeout();
        });
    }, [clearCaptchaState, generateCaptchaSignal]);

    const generateToken = useCallback(() => {
        // E2E skip (lib/utils/e2eSkipKeys.ts): when the test runner has supplied the
        // backend's skip key, the API client sends it as `justsalad-secret-key` and the
        // backend's CaptchaService returns success BEFORE looking at the token — so no
        // token is needed and none is requested from the widget (a headless browser
        // cannot solve the visible challenge hCaptcha escalates to). The widget itself
        // mounts and runs untouched; only this request is skipped.
        if (readE2eSkipKeys()?.secretKey) {
            return Promise.resolve('e2e-skip');
        }

        // Retiring the previous request and clearing the stale challenge flag both live in
        // `requestToken()`, adjacent to each other — doing either here as well would be
        // redundant, and splitting them across two functions is how their ordering gets
        // broken later.
        //
        // Stay synchronous in the common case: the widget is normally ready long
        // before any submit, and callers (plus their tests) rely on `clearCaptchaState`
        // + `generateCaptchaSignal` firing on the same tick as the tap.
        if (isWidgetReady) {
            // Explicitly 0 rather than leaving the previous request's value behind —
            // a stale wait time attached to a later failure would misdirect triage.
            timing.lastWaitedForWidgetMs = 0;
            return requestToken();
        }

        return waitForWidget().then(requestToken);
    }, [requestToken]);

    return {
        generateToken
    };
};

export default useCaptchaWeb;
